import { useEffect, useMemo, useState } from "react";

import { useAuth } from "@/auth/useAuth";
import { getRuntimeConfig } from "@/config/runtimeConfig";
import {
  fetchMyAssignedApps,
  type DiscoveryCall,
  type DiscoveryDiagnostics,
} from "@/services/graphService";
import type { AppItem } from "@/types/app";
import { isCiamTenant } from "@/utils/tenant";

export type CatalogState = {
  apps: AppItem[];
  /** Where the tiles came from: runtime config, Graph, or both merged. */
  source: "config" | "graph" | "merged" | "none";
  loading: boolean;
  error: string | null;
  warnings: string[];
  diagnostics: DiscoveryDiagnostics;
  refresh: () => void;
};

function emptyDiagnostics(
  tenantKind: DiscoveryDiagnostics["tenantKind"],
  configAppsCount: number,
): DiscoveryDiagnostics {
  return {
    tenantKind,
    tokenScopesRequested: [],
    tokenAcquired: false,
    calls: [],
    graphAppsCount: 0,
    configAppsCount,
    summary: "Discovery has not run yet.",
    capturedAt: new Date().toISOString(),
  };
}

/**
 * Build the app catalog the dashboard should render.
 *
 * The hook always attempts Microsoft Graph app discovery – even in
 * CIAM – and silently swallows the expected 403 on
 * `/me/appRoleAssignments`. The per-call diagnostics are surfaced
 * through `diagnostics` so the troubleshoot panel can explain
 * exactly why additional apps weren't discovered.
 */
export function useAppCatalog(): CatalogState {
  const { account, acquireGraphToken } = useAuth();
  const cfg = getRuntimeConfig();
  const tenantKind: DiscoveryDiagnostics["tenantKind"] = isCiamTenant()
    ? "ciam"
    : account
      ? "workforce"
      : "unknown";

  const configApps: AppItem[] = useMemo(() => cfg.apps ?? [], [cfg.apps]);

  const [graphApps, setGraphApps] = useState<AppItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [diagnostics, setDiagnostics] = useState<DiscoveryDiagnostics>(() =>
    emptyDiagnostics(tenantKind, configApps.length),
  );
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!account) {
      setLoading(false);
      return;
    }

    let aborted = false;
    const ctl = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      setWarnings([]);

      const scopesRequested = cfg.graphScopes.length
        ? cfg.graphScopes
        : ["User.Read", "User.ReadWrite"];

      const calls: DiscoveryCall[] = [];
      let tokenAcquired = false;
      let tokenError: string | undefined;
      let apps: AppItem[] = [];
      const collectedWarnings: string[] = [];

      try {
        const token = await acquireGraphToken();
        if (!token?.accessToken) {
          tokenError =
            "Silent token acquisition returned null. The user may not have consented to the Graph scopes, or the tenant has no Graph resource available.";
          collectedWarnings.push(
            "No Microsoft Graph access token. Auto-discovery skipped; only VITE_APPS_JSON tiles will be shown.",
          );
        } else {
          tokenAcquired = true;
          const tenantId =
            account?.tenantId ??
            (account?.homeAccountId.split(".").pop() || "");

          const result = await fetchMyAssignedApps(token.accessToken, {
            tenantId,
            excludeAppIds: cfg.clientId ? [cfg.clientId] : [],
            signal: ctl.signal,
          });
          apps = result.apps;
          collectedWarnings.push(...result.warnings);
          calls.push(...result.calls);
        }
      } catch (err) {
        if (!aborted) {
          console.error("[catalog] graph discovery failed", err);
          const msg = err instanceof Error ? err.message : "Failed to load your apps";
          collectedWarnings.push(`Graph discovery threw: ${msg}`);
          if (tenantKind !== "ciam") setError(msg);
        }
      }

      if (aborted) return;

      const summary = buildDiscoverySummary({
        tenantKind,
        tokenAcquired,
        calls,
        graphAppsCount: apps.length,
        configAppsCount: configApps.length,
      });

      setGraphApps(apps);
      setWarnings(collectedWarnings);
      setDiagnostics({
        tenantKind,
        tokenScopesRequested: scopesRequested,
        tokenAcquired,
        tokenError,
        calls,
        graphAppsCount: apps.length,
        configAppsCount: configApps.length,
        summary,
        capturedAt: new Date().toISOString(),
      });
      setLoading(false);
    })();

    return () => {
      aborted = true;
      ctl.abort();
    };
  }, [account, acquireGraphToken, cfg.clientId, cfg.graphScopes, configApps.length, tenantKind, refreshKey]);

  const merged = useMemo(() => mergeCatalogs(configApps, graphApps), [
    configApps,
    graphApps,
  ]);

  const source: CatalogState["source"] =
    graphApps.length > 0 && configApps.length > 0
      ? "merged"
      : graphApps.length > 0
        ? "graph"
        : configApps.length > 0
          ? "config"
          : "none";

  return {
    apps: merged,
    source,
    loading,
    error,
    warnings,
    diagnostics,
    refresh: () => setRefreshKey((n) => n + 1),
  };
}

function buildDiscoverySummary(input: {
  tenantKind: DiscoveryDiagnostics["tenantKind"];
  tokenAcquired: boolean;
  calls: DiscoveryCall[];
  graphAppsCount: number;
  configAppsCount: number;
}): string {
  if (!input.tokenAcquired) {
    return "No Graph token was acquired — auto-discovery skipped. Only VITE_APPS_JSON tiles are shown.";
  }
  const forbidden = input.calls.find((c) => c.status === 403);
  if (forbidden && input.tenantKind === "ciam") {
    return `Graph returned 403 on ${forbidden.label} — expected in CIAM / External ID. Customer identities cannot read /me/appRoleAssignments or /servicePrincipals. Operator must populate VITE_APPS_JSON.`;
  }
  if (forbidden) {
    return `Graph returned 403 on ${forbidden.label}. Ensure the SPA app registration has delegated Application.Read.All (admin consent required).`;
  }
  const unauth = input.calls.find((c) => c.status === 401);
  if (unauth) {
    return `Graph returned 401 on ${unauth.label}. The current token lacks the required scope or has expired.`;
  }
  if (input.graphAppsCount === 0 && input.calls.length > 0) {
    return "Graph calls succeeded but returned zero assignments. The signed-in user has no Enterprise Application assignments in this tenant.";
  }
  return `Discovered ${input.graphAppsCount} app(s) from Graph; ${input.configAppsCount} tile(s) from VITE_APPS_JSON.`;
}

function mergeCatalogs(a: AppItem[], b: AppItem[]): AppItem[] {
  const byId = new Map<string, AppItem>();
  for (const item of a) byId.set(item.id, item);
  // Graph entries override config entries on id collision so the most
  // authoritative data (Entra) wins.
  for (const item of b) byId.set(item.id, item);
  return Array.from(byId.values()).sort((x, y) => x.name.localeCompare(y.name));
}
